import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { find, get, map, pickBy, isEmpty } from "lodash";
import { InputCheckboxField } from "../../../../../components/Shared/InputCheckboxField";
import * as XLSX from "xlsx";
import qs from "qs";
import { request } from "../../../../../utils/Request";
import { utcDateToLocalDate } from "../../../../../utils/Helper";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    consider_all_pages: boolean,
    load_number: boolean,
    loading: boolean,
    unload: boolean,
    cmr: boolean,
    legal_entity: boolean,
    provider: boolean,
    vin_number: boolean,
    model: boolean,
    qty: boolean,
    driver_name: boolean,
    truck_plate_number: boolean,
    status: boolean,
    created_at: boolean,
    actual_loading_time: boolean,
    actual_unloading_time: boolean,
    invoiced_at: boolean,
    invoice_number: boolean,
    efl_price: boolean,
};

const DEFAULT_FORM_STATE = {
    consider_all_pages: true,
    load_number: true,
    loading: true,
    unload: true,
    cmr: true,
    legal_entity: true,
    provider: true,
    vin_number: true,
    model: true,
    qty: true,
    driver_name: true,
    truck_plate_number: true,
    status: true,
    created_at: true,
    actual_loading_time: true,
    actual_unloading_time: true,
    invoiced_at: true,
    invoice_number: true,
    efl_price: true,
};

// @ts-ignore
function AlpInboundLoadExcel(props: IFormProps) {
    const { t, visibility } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    const onSubmit = async (payloads: TInputForm): Promise<void> => {
        const _params: any = props.moduleState;
        const _payloads: any = payloads;

        if ( payloads.consider_all_pages ) {
            delete _params.page;
            delete _params.per_page;
        }

        try {
            const response = await request({
                url: `/suppliers/provider/inbound/loads/archived/list`,
                params: _params,
                paramsSerializer: {
                    serialize: (params: any) => qs.stringify(params)
                },
            });

            delete _payloads.consider_all_pages;
            const columns = pickBy(_payloads, (item: boolean) => ( item ));
            const title: any = getTitles();
            const data: any = [];
            map(response.data.data, (value: any) => {
                const row: any = {
                    load_number: value.load_number,
                    loading: get(value.from_location, ['label']),
                    unload: get(value.to_location, ['label']),
                    cmr: value.cmr,
                    legal_entity: value.legal_entity,
                    provider: get(value, ['provider', 'title'])
                        || get(find(props.dropdowns?.providers || [], { id: get(value, ['provider', 'slug']) || value.source_type }), ['label'])
                        || get(value, ['provider', 'slug'])
                        || value.source_type
                        || '',
                    vin_number: value.alp_inbound_load_orders
                        ?.map(({ order }: any) => get(order, ['vin_number']))
                        .filter((vin: any) => !isEmpty(vin))
                        .join(', ') || '',
                    model: value.alp_inbound_load_orders
                        ?.map(({ order }: any) => {
                            const brandModel = get(order, ['brand_model', 'title']);
                            const versionCode = get(order, ['brand_model', 'version_code']);
                            return brandModel && versionCode ? `${brandModel} - ${versionCode}` : brandModel || '';
                        })
                        .filter((model: any) => !isEmpty(model))
                        .join(', ') || '',
                    qty: value.qty,
                    driver_name: value.driver_name,
                    truck_plate_number: [
                        value.truck_plate_number,
                        value.trailer_plate_number,
                        value.sub_contractor_name
                    ].filter((item: any) => !isEmpty(item)).join(' | '),
                    status: value.status,
                    created_at: value.created_at ? utcDateToLocalDate(value.created_at) : '',
                    actual_loading_time: value.actual_loading_time ? utcDateToLocalDate(value.actual_loading_time) : '',
                    actual_unloading_time: value.actual_unloading_time ? utcDateToLocalDate(value.actual_unloading_time) : '',
                    invoiced_at: value.invoiced_at ? utcDateToLocalDate(value.invoiced_at) : '',
                    invoice_number: value.invoice_number || '',
                    efl_price: value.emil_frey_price || '',
                };

                const _row: any = {};
                map(columns, (_, key: string) => ( _row[key] = row[key] ));
                const arrayOfRow = Object.keys(_row).map((key) => _row[key]);
                data.push(arrayOfRow);
            });

            const _title = map(columns, (_, key: string) => ( title[key] = title[key] ));
            const excel = XLSX.utils.book_new();
            const sheet = XLSX.utils.aoa_to_sheet([_title, ...data]);
            XLSX.utils.book_append_sheet(excel, sheet, t('EF') + ' ' + t('inbound load'));
            XLSX.writeFile(excel, "alp_inbound_loads.xlsx");
        } catch (e: any) {
            props.toastify(t("server error"), "error");
        }
    };

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getTitles = () => {
        return {
            load_number: t("load") + ' #',
            loading: t("loading"),
            unload: t("unloading"),
            cmr: t('cmr'),
            legal_entity: t('legal entity'),
            provider: t('provider'),
            vin_number: t('vin(s)'),
            model: t('model(s)'),
            qty: t('qty'),
            driver_name: t('driver'),
            truck_plate_number: t('truck'),
            status: t('status'),
            created_at: t('announcement date'),
            actual_loading_time: t('loading date'),
            actual_unloading_time: t('unloading date'),
            invoiced_at: t('invocied at'),
            invoice_number: t('invoice number'),
            efl_price: t('efl price'),
        };
    };

    useEffect(() => {
        if ( visibility ) {
            setPanelState(visibility);
            setInitFormState({ ...DEFAULT_FORM_STATE, ...props.moduleState.filters ?? [] });
        }
    }, [visibility]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t("export") } { t('EF') + ' ' + t('inbound load') }</h5>
                            <p>
                                { t("select columns to be exported.") }
                            </p>
                        </div>
                    }
                    visible={ panelState } position="left" onHide={ onClose } blockScroll={ true }
                    style={ { width: '400px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="consider_all_pages"
                                                component={ InputCheckboxField }
                                                label={ t("consider all pages") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="load_number"
                                                component={ InputCheckboxField }
                                                label={ t("load") + ' #' }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="loading"
                                                component={ InputCheckboxField }
                                                label={ t("loading") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="unload"
                                                component={ InputCheckboxField }
                                                label={ t("unloading") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="cmr"
                                                component={ InputCheckboxField }
                                                label={ t('cmr') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="legal_entity"
                                                component={ InputCheckboxField }
                                                label={ t('legal entity') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="provider"
                                                component={ InputCheckboxField }
                                                label={ t('provider') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="vin_number"
                                                component={ InputCheckboxField }
                                                label={ t('vin(s)') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="model"
                                                component={ InputCheckboxField }
                                                label={ t('model(s)') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="qty"
                                                component={ InputCheckboxField }
                                                label={ t('qty') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="driver_name"
                                                component={ InputCheckboxField }
                                                label={ t('driver') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="truck_plate_number"
                                                component={ InputCheckboxField }
                                                label={ t('truck') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="status"
                                                component={ InputCheckboxField }
                                                label={ t('status') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="created_at"
                                                component={ InputCheckboxField }
                                                label={ t('announcement date') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="actual_loading_time"
                                                component={ InputCheckboxField }
                                                label={ t('loading date') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="actual_unloading_time"
                                                component={ InputCheckboxField }
                                                label={ t('unloading date') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="invoiced_at"
                                                component={ InputCheckboxField }
                                                label={ t('invocied at') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="invoice_number"
                                                component={ InputCheckboxField }
                                                label={ t('invoice number') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="efl_price"
                                                component={ InputCheckboxField }
                                                label={ t('efl price') }
                                            />
                                        </Col>
                                    </Row>
                                    <div className="filter filter-card-footer"
                                         style={ { width: '385px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("export") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-file-excel me-2" />
                                            { t("export") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onClose }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("close") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

AlpInboundLoadExcel.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpInboundLoadExcel);

